import { Component, Inject } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { UntypedFormControl, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { ConfirmationDialogData, DialogConfirmationComponent } from '../dialog-confirmation/dialog-confirmation.component';

export interface TrackingLinkDialogData {
  headerText?: string;
  message?: string;
  initialValue?: string;
  confirmButtonText?: string;
  // Shows a secondary button that closes the dialog with TRACKING_LINK_SKIPPED instead of a link
  skipButtonText?: string;
}

export const TRACKING_LINK_SKIPPED = 'skipped';

// AusPost tracking/consignment numbers are alphanumeric, 8-23 characters. Formats vary by
// service: UPU/EMS items use 2 letters + 9 digits + "AU" (e.g. EA123456789AU), eParcel/Parcel
// Post use up to 23 digits (e.g. 99722001654601004300912), Express Post uses 12-23 alphanumeric
// characters, and some consignment IDs are shorter alphanumeric codes (e.g. RYR9367724).
const AUSPOST_CONSIGNMENT_PATTERN = /^[A-Za-z0-9]{8,23}$/;
const AUSPOST_TRACKING_URL_BASE = 'https://auspost.com.au/mypost/track/details/';
const URL_PATTERN = /^https?:\/\/.+/;
// The generic AusPost search page has no tracking code in it, so it's useless as a tracking link.
const AUSPOST_SEARCH_URL_PATTERN = /^(https?:\/\/)?(www\.)?auspost\.com\.au\/mypost\/track\/search\/*$/i;

export function isAusPostSearchUrl(value: string): boolean {
  return AUSPOST_SEARCH_URL_PATTERN.test((value || '').trim());
}

export function ausPostSearchUrlValidator(): ValidatorFn {
  return (control): ValidationErrors | null =>
    isAusPostSearchUrl(control.value) ? { ausPostSearchUrl: true } : null;
}

export function openTrackingCodeRequiredDialog(dialog: MatDialog) {
  return dialog.open(DialogConfirmationComponent, {
    width: '400px',
    data: {
      headerText: 'Tracking Code Required',
      message: 'Enter the customer tracking code.',
      confirmButtonText: 'Okay',
      showCancelButton: false,
    } as ConfirmationDialogData,
  });
}

// Blocks the AusPost search URL from being pasted into a tracking field and shows the popup instead.
export function blockAusPostSearchUrlPaste(event: ClipboardEvent, dialog: MatDialog) {
  if (isAusPostSearchUrl(event.clipboardData?.getData('text'))) {
    event.preventDefault();
    openTrackingCodeRequiredDialog(dialog);
  }
}

function trackingInputValidator(): ValidatorFn {
  return (control): ValidationErrors | null => {
    const value = (control.value || '').trim();
    if (!value) {
      return { required: true };
    }
    if (isAusPostSearchUrl(value)) {
      return { ausPostSearchUrl: true };
    }
    if (AUSPOST_CONSIGNMENT_PATTERN.test(value) || URL_PATTERN.test(value)) {
      return null;
    }
    return { invalidTrackingInput: true };
  };
}

@Component({
    selector: 'app-dialog-tracking-link',
    templateUrl: './dialog-tracking-link.component.html',
    styleUrls: ['./dialog-tracking-link.component.scss'],
    standalone: false
})
export class DialogTrackingLinkComponent {
  trackingLink = new UntypedFormControl('', [Validators.required, trackingInputValidator()]);

  constructor(
    public dialogRef: MatDialogRef<DialogTrackingLinkComponent>,
    @Inject(MAT_DIALOG_DATA) public data: TrackingLinkDialogData,
    private dialog: MatDialog
  ) {
    this.data = this.data || {};
    this.trackingLink.setValue(this.data.initialValue || '');
  }

  isAusPostNumber(): boolean {
    const value = (this.trackingLink.value || '').trim();
    return AUSPOST_CONSIGNMENT_PATTERN.test(value) && !URL_PATTERN.test(value);
  }

  // Resolves a bare AusPost consignment number to a full tracking URL; a full URL is used as-is.
  resolveTrackingUrl(): string | null {
    const value = (this.trackingLink.value || '').trim();
    if (!value || this.trackingLink.invalid) {
      return null;
    }
    return this.isAusPostNumber() ? AUSPOST_TRACKING_URL_BASE + value : value;
  }

  testTracking() {
    if (this.trackingLink.invalid) {
      this.trackingLink.markAsTouched();
      return;
    }
    const url = this.resolveTrackingUrl();
    if (url) {
      window.open(url, '_blank', 'noopener,noreferrer');
    }
  }

  onPaste(event: ClipboardEvent) {
    blockAusPostSearchUrlPaste(event, this.dialog);
  }

  cancel() {
    this.dialogRef.close(null);
  }

  skip() {
    this.dialogRef.close(TRACKING_LINK_SKIPPED);
  }

  confirm() {
    if (this.trackingLink.errors?.ausPostSearchUrl) {
      openTrackingCodeRequiredDialog(this.dialog);
    }
    if (this.trackingLink.invalid) {
      this.trackingLink.markAsTouched();
      return;
    }
    this.dialogRef.close(this.resolveTrackingUrl());
  }
}
